/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1706832000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Feb 02 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "smartsheet",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Smartsheet",
          "depth": 0,
          "id": "connect-to-smartsheet"
        }, {
          "value": "Authenticate with API key",
          "depth": 1,
          "id": "authenticate-with-api-key"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing rows",
          "depth": 1,
          "id": "syncing-rows"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      img: "img",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sheets"
              })
            }), _jsx(_components.td, {
              children: "Sync data to sheets in Smartsheet"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://smartsheet.redoc.ly",
                children: "Smartsheet API Reference"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-smartsheet",
        children: "Connect to Smartsheet"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Smartsheet"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Smartsheet"
        }), " with either a Smartsheet API key or with OAuth."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-api-key",
        children: "Authenticate with API key"
      }), "\n", _jsxs(_components.p, {
        children: ["To retrieve an API key, go to your Smarsheet dashboard.\nSelect ", _jsx(_components.strong, {
          children: "Account"
        }), " > ", _jsx(_components.strong, {
          children: "Apps & Integrations"
        }), " in the side nav."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-smartsheet-apps-integrations.png",
          alt: "Smartsheet Dashboard"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In the ", _jsx(_components.strong, {
          children: "Personal Settings"
        }), " form that appears, select ", _jsx(_components.strong, {
          children: "API Access"
        }), ".\nOn the ", _jsx(_components.strong, {
          children: "API Access"
        }), " tab, you can either generate a new token by clicking ", _jsx(_components.strong, {
          children: "Generate new access token"
        }), " or use an existing one."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-smartsheet-generate-access-token.png",
          alt: "Smartsheet Dashboard"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the access token into ", _jsx(_components.strong, {
          children: "API Key"
        }), " field in Hightouch and then click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nEnter a descriptive name for your destination, and complete setup."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Smartsheet"
        }), " and log into your Smartsheet account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Smartsheet destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Smartsheet destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-rows",
        children: "Syncing rows"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports inserting, upserting, and updating data as rows into Smartsheet sheets."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Read through the ", _jsx(_components.a, {
            href: "#duplicate-rows-in-sheet",
            children: "section below"
          }), " if you're using Upsert mode and duplicates are being created in your sheet."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to rows in Smartsheet on any column in your model and any field in Smartsheet. Ensure the data types of the model column and Smartsheet field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsxs(Tip, {
        children: [_jsxs(_components.p, {
          children: ["We recommend selecting your sheet's ", _jsx(_components.a, {
            href: "https://help.smartsheet.com/articles/504645-primary-column",
            children: "primary column"
          }), " for record matching."]
        }), _jsx("br", {}), _jsxs(_components.p, {
          children: ["Read through the ", _jsx(_components.a, {
            href: "#duplicate-rows-in-sheet",
            children: "section below"
          }), " if the values you're using for record matching start with a leading zero as that can cause duplicates."]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Smartsheet automatically generates an identifier for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to columns within your selected Smartsheet sheet."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in Smartsheet with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the row in Smartsheet"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced record from your Smartsheet sheet"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "duplicate-rows-in-sheet",
        children: "Duplicate rows in sheet"
      }), "\n", _jsxs(_components.p, {
        children: ["As explained in the ", _jsx(_components.a, {
          href: "https://help.smartsheet.com/learning-track/level-1-get-started/columns#:~:text=Values%20starting%20with%20zero",
          children: "Values starting with zero"
        }), " section, Smartsheet needs to add a leading apostrophe to any values that start with a leading zero. This process can cause duplicates when updating a sheet via API, so we recommend avoiding any leading zeros in your ", _jsx(_components.a, {
          href: "#record-matching",
          children: "record matching"
        }), " values."]
      }), "\n", _jsxs(_components.p, {
        children: ["Furthermore, ", _jsx(_components.a, {
          href: "#supported-syncing",
          children: "Upsert mode"
        }), " might create duplicates in your sheet if you run your sync too frequently (such as every 24 hours or more often). This is because it calls Smartsheet's ", _jsx(_components.a, {
          href: "https://smartsheet.redoc.ly/tag/search/#operation/list-search-sheet",
          children: "Search sheet"
        }), " API endpoint, which might not return rows that were added or edited during the past 24 hours."]
      }), "\n", _jsx(_components.p, {
        children: "To avoid this, you can schedule your sync so it doesn't run as frequently or you can replace your Upsert mode sync with a two-part setup:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "for Update mode: one model, sync (set to Update mode), and Smartsheet sheet"
        }), "\n", _jsx(_components.li, {
          children: "for Insert mode: another model, sync (set to Insert mode), and Smartsheet sheet"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The data from the \"Insert mode\" sheet can then be synced to the \"Update mode\" sheet by a ", _jsx(_components.a, {
          href: "https://help.smartsheet.com/articles/2479626-automatically-copy-rows-between-sheets",
          children: "copy rows"
        }), " Smartsheet workflow. If the workflow is set up correctly, this eliminates the risk of duplicate rows being created in your \"Update mode\" sheet."]
      }), "\n", "\n", _jsx(_components.h4, {
        id: "errorcode-1051",
        children: "errorCode: 1051"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["This error can happen if you try syncing data to ", _jsx(_components.a, {
          href: "https://help.smartsheet.com/articles/522077-locking-unlocking-columns-and-rows",
          children: "locked columns or rows"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
